import { KeyboardEventHandler, useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { PopoverProps } from 'types/ui'
import { ClientGate } from './client-gate'

export { usePopover } from '@hooks/popover'

export function Popover({
  id,
  visible,
  className,
  children,
  arrow = false,
  hide,
  closeable = false,
  setPopperElement,
  setArrowElement,
  popperAttributes,
  popperStyles,
  arrowStyles,
  unmounting,
}: PopoverProps) {
  const popperPlacement = popperAttributes?.['data-popper-placement']
  const popperClass = useMemo<string>(
    () =>
      classNames(
        className,
        'z-[20] popover popover-shadow p-3 text-sm rounded text-slate-200 bg-stone-800 dark:bg-stone-700',
        'max-w-xs xxs:max-w-sm sm:max-w-lg',
        {
          'animate-fade-in': unmounting && visible,
          'transition-opacity duration-200 ease-in-out': !unmounting,
          'opacity-0 pointer-events-none': !unmounting && !visible,
          'opacity-100': !unmounting && visible,
        }
      ),
    [className, unmounting, visible]
  )

  const arrowClass = useMemo<string | undefined>(() => {
    if (arrow && popperPlacement) {
      return classNames({
        '-top-1.5': popperPlacement === 'bottom',
        '-bottom-1.5': popperPlacement === 'top',
        '-right-1.5': popperPlacement === 'left',
        '-left-1.5': popperPlacement === 'right',
      })
    }
  }, [arrow, popperPlacement])

  const handleKeyUp = useCallback<KeyboardEventHandler<HTMLDivElement>>(
    (e) => {
      if (visible && e.key === 'Escape') hide?.()
    },
    [hide, visible]
  )

  if (unmounting && !visible) return null
  return (
    <ClientGate>
      <div
        role="tooltip"
        aria-label="popover"
        aria-roledescription="tip"
        {...popperAttributes}
        id={id}
        ref={setPopperElement}
        style={popperStyles}
        className={popperClass}
        onKeyUp={handleKeyUp}
      >
        {children}
        {arrow && (
          <div
            ref={setArrowElement}
            aria-hidden
            style={arrowStyles}
            className={arrowClass}
            data-popper-arrow
          >
            <div className="w-3 h-3 rotate-45 bg-stone-800 dark:bg-stone-700"></div>
          </div>
        )}
        {closeable && visible && (
          <button
            type="button"
            aria-controls={id}
            aria-label="close"
            tabIndex={1}
            onClick={hide}
            className="absolute top-0 right-0 px-2 text-lg font-bold text-center text-white bg-transparent rounded-sm outline-none ring-2 ring-transparent focus:ring-white !border-none"
          >
            x
          </button>
        )}
      </div>
    </ClientGate>
  )
}
